/* eslint-disable default-case, no-restricted-syntax, prefer-const */
import { get, intersectionBy, isNil, uniqBy } from 'lodash'
import moment from 'moment'

import { ALL_MEMBER_TYPES } from '../constants/messages'
import { calculateAge } from '../functions/dateTime'

export const sortList = (list = []) => {
  list.sort((a, b) => {
    // sort by tnc
    const typeA = get(a, 'relatedQuestion.type') === 'TermsAndConditions' ? 1 : 0
    const typeB = get(b, 'relatedQuestion.type') === 'TermsAndConditions' ? 1 : 0
    if (typeA !== typeB) {
      return typeA - typeB
    }
    const orgLevelA = a.financialStatusLevel || a.organisationLevel
    const orgLevelB = b.financialStatusLevel || b.organisationLevel
    if (orgLevelA !== orgLevelB) {
      return orgLevelA - orgLevelB
    }
    const lockA = a.isLocked ? 0 : 1
    const lockB = b.isLocked ? 0 : 1
    if (lockA !== lockB) {
      return lockA - lockB
    }
    if ('orderNumber' in a && 'orderNumber' in b) {
      return a.orderNumber - b.orderNumber
    }
    return a.order - b.order
  })
  return list
}

export const sortProducts = (list = []) => {
  list.sort((a, b) => {
    const orgLevelA = a.organisationLevel
    const orgLevelB = b.organisationLevel
    if (orgLevelA !== orgLevelB) {
      return orgLevelA - orgLevelB
    }
    const lockA = a.isLocked ? 0 : 1
    const lockB = b.isLocked ? 0 : 1
    if (lockA !== lockB) {
      return lockA - lockB
    }
    return a.orderNumber - b.orderNumber
  })
  return list
}

export const validAge = (ageRules, birthdate, timezone) => {
  if (!ageRules) {
    return true
  }
  // verify showToAllAge property
  const isShowToAllAges = (!isNil(ageRules.showToAllAge) && ageRules.showToAllAge) || Object.keys(ageRules).length === 0

  if (isShowToAllAges) return true

  // verify age
  const ruleDate = {
    min: getAgeDate(ageRules, true, timezone),
    max: getAgeDate(ageRules, false, timezone),
  }
  const ageRange = {
    min: ageRules.minimumAge || 0,
    max: ageRules.maximumAge || 999,
  }

  const minAgeOnDate = calculateAge(birthdate, timezone, ruleDate.min)
  const maxAgeOnDate = calculateAge(birthdate, timezone, ruleDate.max)
  const isValidAge =
    minAgeOnDate > 0 && maxAgeOnDate > 0 && minAgeOnDate >= ageRange.min && maxAgeOnDate <= ageRange.max

  return !!isValidAge
}

export const validFormAge = (ageRestriction, birthdate, timezone) => {
  if (!ageRestriction) return true
  const { minAge, maxAge, ageCalculationMode, ageCalculationDate } = ageRestriction

  if (isNil(minAge) && isNil(maxAge)) return true

  let calcDate
  if (ageCalculationMode === 'specific_date' && ageCalculationDate) {
    calcDate = moment.tz(ageCalculationDate, timezone)
  } else {
    // Default to registration date
    calcDate = moment.tz(timezone).endOf('day')
  }

  const age = calculateAge(birthdate, timezone, calcDate)
  if (age < 0) return false
  if (!isNil(minAge) && age < minAge) return false
  if (!isNil(maxAge) && age > maxAge) return false
  return true
}

const getAgeDate = (rule, isMin, timezone) => {
  const option = isMin ? rule.minAgeOption : rule.maxAgeOption
  if (isNil(option)) return moment.tz(timezone)

  let date
  switch (option) {
    case 'Specific Date':
    case 'Season Start Date':
    case 'Season End Date':
      date = isMin ? rule.minAgeSpecificDate : rule.maxAgeSpecificDate
      break
    case 'Registration Date':
      return moment.tz(timezone).endOf('day')
  }

  return moment.tz(date, timezone)
}

export const validGender = (genderRule, gender) => (genderRule ? genderRule === gender : true)

export const toAllMemberTypes = memberTypes => !!memberTypes.find(mt => mt.id === ALL_MEMBER_TYPES)

const validMember = (memberTypesRule, memberTypes) =>
  isNil(memberTypesRule) ||
  isNil(memberTypes) ||
  toAllMemberTypes(memberTypesRule) ||
  intersectionBy(memberTypesRule, memberTypes, obj => obj.sfid || obj.id).length > 0

export const validAvailableDate = (fromDate, toDate, timezone) => {
  if (fromDate && !moment.tz(fromDate, timezone).startOf('day').isSameOrBefore(moment.tz(timezone))) {
    return false
  }
  if (toDate && !moment.tz(toDate, timezone).endOf('day').isSameOrAfter(moment.tz(timezone))) {
    return false
  }
  return true
}

export const validateProduct = product => {
  const { status, availableFromDate, availableToDate } = product
  return status === 'Active' && validAvailableDate(availableFromDate, availableToDate)
}

// biome-ignore lint/complexity/useOptionalChain: TODO
const findProductId = (sfid, formProducts) => (formProducts.find(p => p.sfid === sfid) || {}).id

const depdendantProductsFulfilled = (
  question = {},
  newProducts = {},
  formProducts = [],
  newSubEventProducts = {},
  newAddOnProducts = {}
) => {
  const allProducts = {
    ...newProducts,
    ...newSubEventProducts,
    ...newAddOnProducts,
  }

  if (!question || !Object.keys(question).length) {
    return true
  }
  const { displayRules } = question
  if (!displayRules || !Object.keys(displayRules).length) {
    return true
  }
  const { dependantProducts } = displayRules
  // biome-ignore lint/complexity/useOptionalChain: TODO(biome-2.4): newly detected in 2.4 upgrade
  if (!dependantProducts || !dependantProducts.length) {
    return true
  }
  if (!allProducts || !Object.values(allProducts).length) {
    return true
  }
  const chosenProducts = []
  for (const [sfid, product] of Object.entries(allProducts)) {
    /* eslint-disable-next-line radix */
    // biome-ignore lint/complexity/useOptionalChain: TODO
    if (product && product.checked && product.formProductId && parseInt(product.quantity) > 0) {
      const pid = findProductId(sfid, formProducts)
      if (pid) {
        chosenProducts.push(pid)
      } else if (newAddOnProducts[sfid]) {
        chosenProducts.push(product.formProductId)
      } else if (newSubEventProducts[sfid]) {
        chosenProducts.push(product.formProductId)
      }
    }
  }
  let hasChosen = false
  for (const product of dependantProducts) {
    if (chosenProducts.includes(product.id)) {
      hasChosen = true
      break
    }
  }
  return chosenProducts.length ? hasChosen : true
}

export const displayRuleFilter = (list = [], formData, formProducts) => {
  const filteredList = list.filter(
    ({ org, displayRules, availableFromDate, availableToDate }) =>
      validAge(displayRules, formData.birthdate, get(formData, `timezones.${org}`)) &&
      // biome-ignore lint/complexity/useOptionalChain: TODO
      validGender((displayRules || {}).gender, formData.gender) &&
      // biome-ignore lint/complexity/useOptionalChain: TODO
      validMember((displayRules || {}).memberTypes, formData.memberTypes) &&
      validAvailableDate(availableFromDate, availableToDate, get(formData, `timezones.${org}`))
  )

  const filteredDependantProductsList = filteredList.filter(question =>
    depdendantProductsFulfilled(
      question,
      formData.newProducts,
      formProducts,
      formData.newSubEventProducts,
      formData.newAddOnProducts
    )
  )
  return sortList(filteredDependantProductsList)
}

export const mapSubEvents = (subEvents = [], paidMap = {}) => {
  const allProducts = []
  const initMap = []
  const filteredPaid = []
  // create parent/child product id map
  subEvents.forEach(s => {
    const products = []
    s.products.forEach(p => {
      products.push(p.sfid)
      allProducts.push(p)
    })

    s.relatedProducts.forEach(r => {
      allProducts.push(r)
      initMap.push({
        sfid: r.sfid,
        products,
      })
    })
  })

  // get list of paid product ids
  const paidProducts = Object.keys(paidMap).reduce((a, p) => {
    a.push(p)
    return a
  }, [])

  allProducts.forEach(ap => {
    if (paidProducts.includes(ap.sfid)) {
      filteredPaid.push(ap.sfid)
    }
  })

  // merge related product id maps together
  const mergeMap = initMap.reduce((a, cur) => {
    const keys = a.reduce((n, item, i) => (item.sfid === cur.sfid ? i : n), -1)

    if (keys >= 0) {
      a[keys].products = [...a[keys].products, ...cur.products]
    } else {
      const obj = {
        sfid: cur.sfid,
        products: cur.products,
      }
      a.push(obj)
    }

    return a
  }, [])

  return mergeMap.reduce((a, m) => {
    a[m.sfid] = m.products
    return a
  }, {})
}

export const filterSubEvents = (subEvents = [], formData, paidMap = {}, formType, mainProducts) => {
  const filteredSubs = []
  const allProducts = [...mainProducts]
  const filteredPaid = []
  const mappedSubEvents = mapSubEvents(subEvents, paidMap)

  subEvents.forEach(s => s.products.forEach(p => allProducts.push(p)))

  // get list of paid product ids
  const paidProducts = Object.keys(paidMap).reduce((a, p) => {
    a.push(p)
    return a
  }, [])

  allProducts.forEach(ap => {
    if (paidProducts.includes(ap.sfid) && ap.allowMultiplePurchase === false) {
      filteredPaid.push(ap.sfid)
    }
  })

  // get list of selected product ids
  const selectedProducts = Object.keys(formData.newProducts || []).reduce((a, s) => {
    if (formData.newProducts[s].checked) {
      a.push(s)
    }
    return a
  }, [])

  // create filtered products list
  const initFilteredProducts = Object.keys(mappedSubEvents).reduce((a, m) => {
    if (selectedProducts.includes(m) || filteredPaid.includes(m)) {
      const ap = allProducts.filter(p => mappedSubEvents[m].includes(p.sfid))
      // biome-ignore lint/performance/noAccumulatingSpread: TODO
      a = [...a, ...ap]
    }
    return a
  }, [])

  // filter products by display rules
  const filteredProducts = displayRuleFilter(
    uniqBy(
      initFilteredProducts.map(p => ({ ...p, org: p.definedById })),
      'sfid'
    ),
    formData,
    formType === 'event'
  )

  const fp = []
  filteredProducts.forEach(f => fp.push(f.sfid))

  // create filtered sub event list
  subEvents.forEach(s => {
    const rpList = []
    s.relatedProducts.forEach(rp => rpList.push(rp.sfid))

    const availableProducts = s.products.reduce((a, p) => {
      const isAvailable = fp.includes(p.sfid)
      a.push(isAvailable)
      return a
    }, [])

    if (availableProducts.includes(true)) {
      rpList.forEach(rpl => {
        if (selectedProducts.includes(rpl) || filteredPaid.includes(rpl)) {
          filteredSubs.push(s)
        }
      })
    }
  })

  return {
    filteredProducts,
    filteredSubs: uniqBy(filteredSubs, 'id'),
  }
}
